import React, { useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaUsers } from 'react-icons/fa';
import { showSuccess, showError, showInfo } from '../utils/toast';
import { authFetch } from '../utils/api';
import useRolePermissions, { hasPermission } from '../hooks/useRolePermissions';

const API_ADD = 'https://dev.sliitleo.org/Backend/addregistration.php';
const API_INSTALLATION = 'https://dev.sliitleo.org/Backend/installation_reg.php';

export default function AddRegistration() {
  const user = useMemo(() => {
    return JSON.parse(localStorage.getItem('user')) || {
      SLIITID: '',
      name: 'Unknown User',
      role: 'Member',
      image: '/user.png',
    };
  }, []);

  // Submit previously had no gate beyond page access. Fallback is this
  // page's own route access list (App.js's /add-registration allowedRoles),
  // so nothing anyone could already do stops working until an admin
  // explicitly narrows it in Role & Path Management.
  const addRegPerm = useRolePermissions(user.role);
  const canSubmitRegistration = hasPermission(
    'add_registration_submit',
    addRegPerm,
    user.role,
    ['System Admin', 'president', 'secretary', 'Club Performence Evaluator', 'President Program Coordinator', 'Club Director', 'treasurer', 'Club Secretary', 'Vice President', 'Club President', 'Club President Program Coordinator', 'Activity Director', 'Team Head', 'Club IT Department', 'Installation sub-committee member', 'Club Reporting Sub-Committee', 'Club Committee Member']
  );

  const [submitting, setSubmitting] = useState(false);
  // Presentational only -- mirrors the existing toast messages into an inline
  // banner per the design system; does not change any validation/fetch logic.
  const [banner, setBanner] = useState(null); // { type: 'success' | 'error', text }

  const [form, setForm] = useState({
    sliitId: '',
    name: '',
    faculty: '',
    nic: '',
    mobile: '',
    email: '',
    prevCeremony: '',
    attending: '',
    food: '',
    year: '',
    amount: '',
    Verify: 1,
    VerifiedBy: user.SLIITID || '',
    VerifiedDate: new Date().toISOString().slice(0, 16), // yyyy-MM-ddTHH:mm
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleRadio = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  async function readJsonSafe(res) {
    const txt = await res.text();
    try { return { json: JSON.parse(txt), raw: txt, ok: res.ok, status: res.status }; }
    catch { return { json: null, raw: txt, ok: res.ok, status: res.status }; }
  }

  async function sendVerifiedEmail({ regid }) {
    if (!regid) {
      showInfo('Saved without email: could not detect RegID from server response.');
      return;
    }

    const params = new URLSearchParams();
    params.set('action', 'verify');
    params.set('RegID', String(regid));
    params.set('Verify', '1');
    params.set('VerifiedBy', String(user.SLIITID || ''));
    params.set('VerifiedByName', String(user.name || 'Admin'));
    params.set('SendEmail', '1');     // email on verify only
    params.set('EmailType', 'verified');
    params.set('Debug', '1');

    const res = await authFetch(API_INSTALLATION, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
    const { json, raw, ok, status } = await readJsonSafe(res);

    if (!ok || !json?.success) {
      const msg = json?.message || json?.error || `HTTP ${status}`;
      showError('Saved, but email failed: ' + msg);
      console.warn('Email verify POST failed:', { status, raw, json });
      return;
    }

    if (json.email_sent === true) {
      showSuccess('Registration saved & verification email sent ✅');
    } else if (json.email_error) {
      showInfo('Registration saved. Email could not be sent: ' + json.email_error);
    } else {
      showSuccess('Registration saved & marked verified.');
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !canSubmitRegistration) return;
    setBanner(null);

    // Validate required
    if (!form.sliitId || !form.name || !form.email || !form.amount) {
      showError('Please fill in SLIIT ID, Name, Email, and Amount.');
      setBanner({ type: 'error', text: 'Please fill in SLIIT ID, Name, Email, and Amount.' });
      return;
    }

    // Coerce numbers
    const amountNum = parseFloat(String(form.amount).replace(/[^\d.]/g, ''));
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      showError('Please enter a valid amount.');
      setBanner({ type: 'error', text: 'Please enter a valid amount.' });
      return;
    }
    const yearNum = form.year ? Number(form.year) : null;

    // Build payload with correct types
    const payload = {
      InstallationID: 1,                            // INT
      Email: form.email || null,
      SLIITID: form.sliitId || null,
      Name: form.name || null,
      NIC: form.nic || null,
      Contact: form.mobile || null,
      Faculty: form.faculty || null,
      Year: yearNum,                                // INT or null
      FoodPref: form.food || null,
      PrevStatus: form.prevCeremony || null,
      PartStatus: form.attending || null,

      FileURL: null,
      Amount: amountNum,                            // DECIMAL/INT
      Bank: 'Cash-in-hand',
      PaymenMethod: 'Cash',                         // keep key spelling as backend expects

      Json: null,

      Verify: 1,                                    // INT
      VerifiedBy: user.SLIITID || null,            // likely string/ID
      CheckIn: 0,                                   // INT
      CheckedBy: null,

      RegDate: new Date().toISOString().slice(0, 10), // 'YYYY-MM-DD'
      VerifiedDate: ((form.VerifiedDate || new Date().toISOString().slice(0, 16)).replace('T', ':') + ':00')
        .replace('::', ':')                         // 'YYYY-MM-DD HH:MM:SS'

      ,ReportReason: null,
      ReportedBy: null,
      ReportedDate: null,
      CheckedDate: null,

      // ✅ FIX: send INT, not string
      respondFrom: 2,                               // 1 = Google Form, 2 = Web Form (choose any mapping)

      paymentId: null,
      transactionId: null,
      merchId: null,
    };

    try {
      setSubmitting(true);

      const res = await authFetch(API_ADD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const { json, raw, ok, status } = await readJsonSafe(res);

      if (!ok || !json) {
        throw new Error(`HTTP ${status}: ${raw?.slice?.(0, 200) || 'Invalid response'}`);
      }
      if (!json.success) {
        // bubble server-side reason (like column mismatch) to the UI
        throw new Error(json.message || 'Backend failed to create registration');
      }

      // best-effort: read new RegID from various shapes
      const regid =
        json.regid ??
        json.RegID ??
        json.id ??
        (json.data && (json.data.regid || json.data.RegID || json.data.id)) ??
        null;

      await sendVerifiedEmail({ regid });
      setBanner({ type: 'success', text: 'Registration saved successfully.' });

      // reset form
      setForm((prev) => ({
        ...prev,
        sliitId: '',
        name: '',
        faculty: '',
        nic: '',
        mobile: '',
        email: '',
        prevCeremony: '',
        attending: '',
        food: '',
        year: '',
        amount: '',
        Verify: 1,
        VerifiedBy: user.SLIITID || '',
        VerifiedDate: new Date().toISOString().slice(0, 16),
      }));
    } catch (err) {
      console.error('AddRegistration submit failed:', err);
      const msg = 'Error submitting registration: ' + (err?.message || String(err));
      showError(msg);
      setBanner({ type: 'error', text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="page-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
            <span
              style={{
                width: 44, height: 44, borderRadius: 'var(--radius-md)', flexShrink: 0,
                display: 'grid', placeItems: 'center', background: 'var(--accent)', color: '#fff',
              }}
            >
              <FaUsers size={18} />
            </span>
            <div>
              <h1>Installation <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>/ Add Registration</span></h1>
              <p className="page-desc">Register a member for the Installation Ceremony.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="panel" style={{ padding: 'var(--sp-5)' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
            {/* SLIIT ID */}
            <div className="field">
              <label className="field-label" htmlFor="reg-sliit-id">SLIIT ID<span className="req">*</span></label>
              <input
                id="reg-sliit-id"
                name="sliitId"
                value={form.sliitId}
                onChange={handleChange}
                type="text"
                placeholder="Enter SLIIT ID"
                autoComplete="off"
                required
              />
            </div>

            {/* Member Name */}
            <div className="field">
              <label className="field-label" htmlFor="reg-name">Member Name<span className="req">*</span></label>
              <input
                id="reg-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                type="text"
                placeholder="Full Name"
                required
              />
            </div>

            {/* Faculty */}
            <div className="field">
              <label className="field-label" htmlFor="reg-faculty">Faculty</label>
              <select
                id="reg-faculty"
                name="faculty"
                value={form.faculty}
                onChange={handleChange}
              >
                <option value="">Select Faculty</option>
                <option value="Business Management">Business Management</option>
                <option value="Humanities & Sciences">Humanities & Sciences</option>
                <option value="Computing">Computing</option>
                <option value="Engineering">Engineering</option>
                <option value="School of Architecture">School of Architecture</option>
              </select>
            </div>

            {/* NIC */}
            <div className="field">
              <label className="field-label" htmlFor="reg-nic">NIC</label>
              <input
                id="reg-nic"
                name="nic"
                value={form.nic}
                onChange={handleChange}
                type="text"
                placeholder="Enter NIC"
              />
            </div>

            {/* Mobile */}
            <div className="field">
              <label className="field-label" htmlFor="reg-mobile">Mobile Number</label>
              <input
                id="reg-mobile"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                type="text"
                placeholder="Mobile number"
              />
            </div>

            {/* Email */}
            <div className="field">
              <label className="field-label" htmlFor="reg-email">Email<span className="req">*</span></label>
              <input
                id="reg-email"
                name="email"
                value={form.email}
                onChange={handleChange}
                type="email"
                placeholder="Email address"
                required
              />
            </div>

            {/* Prev Ceremony */}
            <fieldset className="field md:col-span-3" style={{ border: 'none', padding: 0 }}>
              <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>Attended Club Installation before?</legend>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="radio" name="prevCeremony" value="Yes" checked={form.prevCeremony === 'Yes'} onChange={handleRadio} />
                  Yes
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="radio" name="prevCeremony" value="No" checked={form.prevCeremony === 'No'} onChange={handleRadio} />
                  No
                </label>
              </div>
            </fieldset>

            {/* Attending */}
            <fieldset className="field md:col-span-3" style={{ border: 'none', padding: 0 }}>
              <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>Attending the Ceremony?</legend>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="radio" name="attending" value="Yes" checked={form.attending === 'Yes'} onChange={handleRadio} />
                  Yes
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="radio" name="attending" value="No" checked={form.attending === 'No'} onChange={handleRadio} />
                  No
                </label>
              </div>
            </fieldset>

            {/* Year */}
            <div className="field">
              <label className="field-label" htmlFor="reg-year">Year</label>
              <select
                id="reg-year"
                name="year"
                value={form.year}
                onChange={handleChange}
              >
                <option value="">Select Year</option>
                <option value="1">1st Year</option>
                <option value="2">2nd Year</option>
                <option value="3">3rd Year</option>
                <option value="4">4th Year</option>
              </select>
            </div>

            {/* Food */}
            <div className="field">
              <label className="field-label" htmlFor="reg-food">Food Preference</label>
              <select
                id="reg-food"
                name="food"
                value={form.food}
                onChange={handleChange}
              >
                <option value="">Food Preference</option>
                <option value="Veg">Vegetarian</option>
                <option value="Non-Veg">Non-Vegetarian</option>
              </select>
            </div>

            {/* Amount */}
            <div className="field">
              <label className="field-label" htmlFor="reg-amount">Amount<span className="req">*</span></label>
              <input
                id="reg-amount"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                type="text"
                placeholder="Enter amount"
                required
              />
            </div>

            {/* Verified By */}
            <div className="field">
              <label className="field-label" htmlFor="reg-verified-by">Verified By</label>
              <select
                id="reg-verified-by"
                name="VerifiedBy"
                value={form.VerifiedBy}
                onChange={handleChange}
              >
                <option value={user.SLIITID || ''}>{user.name}</option>
              </select>
            </div>

            {/* Verified Date */}
            <div className="field">
              <label className="field-label" htmlFor="reg-verified-date">Verified Date</label>
              <input
                id="reg-verified-date"
                type="datetime-local"
                name="VerifiedDate"
                value={form.VerifiedDate}
                onChange={handleChange}
              />
            </div>
          </div>

          {banner && (
            <div
              className={banner.type === 'success' ? 'success-banner' : 'error-banner'}
              style={{ marginBottom: 'var(--sp-4)' }}
              role="status"
            >
              {banner.text}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {canSubmitRegistration ? (
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary"
                style={{ minWidth: 220, justifyContent: 'center' }}
              >
                {submitting ? (
                  <>
                    <span className="spinner" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Submit Registration
                  </>
                )}
              </button>
            ) : (
              <p className="field-hint">You don't have permission to submit a new registration.</p>
            )}
          </div>
        </form>
      </main>
    </>
  );
}
